import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import userService from '../../services/UserService.js';
import axiosInstance from '../../config/axios.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import { Users, Plus, Filter, Eye, Pencil, Trash2, UserX, Building2 } from "lucide-react";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'clients'
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  // Get permissions from hook
  const { permissions, canSeeClientTab, userRole } = usePermissions();
  const canCreate = permissions.users.canCreate;
  const canEdit = permissions.users.canEdit;
  const canDelete = permissions.users.canDelete;

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter, roleFilter, activeTab]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchUsers();
    }
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      setRoles(response.data.data);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'clients') {
        // Fetch only clients
        const filters = {};
        if (search) filters.search = search;
        if (statusFilter) filters.status = statusFilter;
        const response = await userService.getClients(currentPage, perPage, filters);
        setUsers(response.data || []);
        setTotalPages(response.pagination?.pages || 1);
      } else {
        // Fetch regular users (excluding clients)
        const filters = {};
        if (search) filters.search = search;
        if (statusFilter) filters.status = statusFilter;
        if (roleFilter) filters.role_id = roleFilter;
        
        // Get Client role ID to exclude
        const clientRole = roles.find(r => r.role_name === 'Client');
        if (clientRole) {
          filters.exclude_role_id = clientRole.id;
        }
        
        const response = await userService.getAll(currentPage, perPage, filters);
        setUsers(response.data);
        setTotalPages(response.pagination.pages);
      }
    } catch (err) {
      Alert.error('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, userStatus, userName) => {
    if (!canDelete) {
      Alert.error('You do not have permission to delete users');
      return;
    }
    
    const isInactive = userStatus === 'inactive';
    const confirmTitle = isInactive ? 'Permanently Delete User' : 'Deactivate User';
    const confirmMessage = isInactive
      ? `Are you sure you want to permanently delete "${userName}"? This action cannot be undone and will remove all user data from the system.`
      : `Are you sure you want to deactivate "${userName}"? The user will be marked as inactive and can be reactivated later.`;
    const confirmed = await Alert.confirm(confirmMessage, confirmTitle);
    if (confirmed) {
      try {
        const response = await userService.delete(userId);
        if (response.action === 'deleted') {
          Alert.success('User permanently deleted from system!');
        } else {
          Alert.success('User deactivated successfully! Delete again to permanently remove.');
        }
        if (users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchUsers();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const getProfileImageUrl = (user) => {
    if (user.profile_photo_base64) {
      return `data:image/jpeg;base64,${user.profile_photo_base64}`;
    }
    return userService.getProfilePhoto(user.user_id);
  };

  const toggleDropdown = (userId) => {
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
  };

  const hasActiveFilters = search || statusFilter || roleFilter;

  // Filter roles to exclude 'Client' for the users tab dropdown
  const filteredRoles = activeTab === 'users' 
    ? roles.filter(r => r.role_name !== 'Client')
    : roles;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <Users className="w-5 h-5 text-buttonBg" />
                        {activeTab === 'clients' ? 'Clients' : 'Users'}
                      </h1>
                    </div>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {canCreate && (
                        <button
                          onClick={() => navigate('/users/add')}
                          className="
                              relative overflow-hidden
                              group flex items-center justify-center gap-2
                              px-3 sm:px-3 py-2.5 sm:py-2
                              text-xs sm:text-sm
                              rounded-lg
                              border border-buttonBg
                              bg-buttonBg text-buttonTxt
                              font-medium whitespace-nowrap

                              transition-colors duration-300 ease-out
                              hover:bg-transparent hover:text-buttonBg
                            "
                          >
                            {/* COLLAPSING BACKGROUND */}
                            <span
                              className="
                                absolute inset-0
                                bg-buttonBg
                                scale-x-100
                                group-hover:scale-x-0
                                origin-center
                                transition-transform duration-300 ease-out
                                z-0
                              "
                            />

                            {/* ICON */}
                            <Plus
                              className="
                                w-4 h-4 relative z-10
                                transition-transform duration-300 ease-out
                                group-hover:scale-90
                              "
                            />

                            <span className="relative z-10">Add {activeTab === 'clients' ? 'Client' : 'User'}</span>
                          <span className="sm:hidden">Add</span>
                        </button>
                      )}
                    </div>
                </div>

                {/* Tabs - Only show if user can see client tab */}
                {canSeeClientTab && (
                  <div className="flex border-b border-gray-200 mb-4">
                    <button
                      onClick={() => handleTabChange('users')}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'users'
                          ? 'border-buttonBg text-buttonBg'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      Users
                    </button>
                    <button
                      onClick={() => handleTabChange('clients')}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'clients'
                          ? 'border-buttonBg text-buttonBg'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Building2 className="w-4 h-4" />
                      Clients
                    </button>
                  </div>
                )}

                {/* Filters */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-0 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                      <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                    </div>
                  </div>
                  <div className='hidden sm:block'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'>
                        {/* Search */}
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder={activeTab === 'clients' ? "Search by name, email, organization..." : "Search by name or email..."}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>

                        {/* Mobile Filter Toggle */}
                        <button
                          onClick={() => setShowMobileFilters(!showMobileFilters)}
                          className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-sm hover:bg-gray-50"
                        >
                          <Filter size={18} />
                          Filters
                        </button>

                        {/* Desktop Filters */}
                        <div className="hidden lg:flex gap-3 flex-wrap">
                          <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                        <div>
                          {activeTab === 'users' && (
                            <select
                              value={roleFilter}
                              onChange={(e) => setRoleFilter(e.target.value)}
                              className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              <option value="">All Roles</option>
                              {filteredRoles.map((role) => (
                                <option key={role.id} value={role.id}>{role.role_name}</option>
                              ))}
                            </select>
                          )}

                          {hasActiveFilters && (
                            <button
                              onClick={clearFilters}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                            >
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                  {/* Mobile Filters Dropdown */}
                  {showMobileFilters && (
                    <div className="lg:hidden mt-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>

                      {activeTab === 'users' && (
                        <select
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="">All Roles</option>
                          {filteredRoles.map((role) => (
                            <option key={role.id} value={role.id}>{role.role_name}</option>
                          ))}
                        </select>
                      )}

                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="bg-white rounded-sm shadow-sm">
                  <div className="p-4 sm:p-6 lg:p-0">

                    {/* Table View - Desktop */}
                    <div className="hidden lg:block bg-white rounded-sm shadow-sm">
                      {loading ? (
                        <div className="p-12 text-center text-gray-600">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                          Loading {activeTab === 'clients' ? 'clients' : 'users'}...
                        </div>
                      ) : users.length === 0 ? (
                        <div className="p-12 text-center text-gray-600">
                          No {activeTab === 'clients' ? 'clients' : 'users'} found
                        </div>
                      ) : (
                        <>
                          <div className="hidden md:block w-full">
                            <table className="w-full table-fixed border-collapse">
                              <thead>
                                <tr className="bg-primary text-tertiary">
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[7%]">Photo</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[15%]">Name</th>
                                  {activeTab === 'clients' && (
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[15%]">Organization</th>
                                  )}
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[15%]">Email</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Contact</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">City</th>
                                  {activeTab === 'users' && (
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Role</th>
                                  )}
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Created By</th>
                                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((user) => (
                                  <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <img
                                        src={getProfileImageUrl(user)}
                                        alt={user.name}
                                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                              <rect width="40" height="40" fill="#e5e7eb"/>
                                              <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="16" font-weight="bold">
                                                ${user.name.charAt(0).toUpperCase()}
                                              </text>
                                            </svg>
                                          `)}`;
                                        }}
                                      />
                                    </td>
                                    <td className="px-6 py-4">
                                      <button
                                        onClick={() => navigate(`/users/view/${user.user_id}`)}
                                        className="text-sm font-medium text-gray-900 text-left hover:underline"
                                      >
                                        {user.name}
                                      </button>
                                    </td>
                                    {activeTab === 'clients' && (
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-700 truncate max-w-[180px]">
                                          {user.organization_name || '-'}
                                        </div>
                                      </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700 truncate max-w-[180px]">{user.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{user.contact}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{user.city}</div>
                                    </td>
                                    {activeTab === 'users' && (
                                      <td className="px-6 py-4">
                                        <div className="text-sm text-gray-700 text-left">{user.role_name}</div>
                                      </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        user.status === 'active'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {capitalizeStatus(user.status)}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="relative group w-fit">
                                        <span className="text-sm text-gray-700 truncate max-w-[80px] inline-block">
                                          {user.created_by}
                                        </span>
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50 shadow-lg">
                                          {user.created_by}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium relative">
                                      <button
                                        onClick={() => toggleDropdown(user.user_id)}
                                        className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md transition-colors"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                          <circle cx="12" cy="5" r="2"/>
                                          <circle cx="12" cy="12" r="2"/>
                                          <circle cx="12" cy="19" r="2"/>
                                        </svg>
                                      </button>

                                      {activeDropdown === user.user_id && (
                                        <div
                                          ref={dropdownRef}
                                          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                                        >
                                          <button
                                            onClick={() => { navigate(`/users/view/${user.user_id}`); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                          >
                                            <Eye size={16} className="text-buttonBg" />
                                            <span className="text-sm capitalize text-gray-600 font-regular">View Details</span>
                                          </button>
                                          {canEdit && (
                                            <button
                                              onClick={() => { navigate(`/users/edit/${user.user_id}`); setActiveDropdown(null); }}
                                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                            >
                                              <Pencil size={16} className="text-buttonBg" />
                                              <span className="text-sm capitalize text-gray-600 font-regular">Edit</span>
                                            </button>
                                          )}
                                          {canDelete && (
                                            <button
                                              onClick={() => { handleDelete(user.user_id, user.status, user.name); setActiveDropdown(null); }}
                                              className="w-full px-4 py-2 text-left text-sm flex gap-2 items-center"
                                            >
                                              {user.status === 'inactive' ? (
                                                <UserX size={16} className="text-red-600" />
                                              ) : (
                                                <Trash2 size={16} className="text-red-600" />
                                              )}
                                              <span className="text-sm capitalize text-gray-600 font-regular">
                                                {user.status === 'inactive' ? 'Permanently Delete' : 'Deactivate'}
                                              </span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                            <div className="text-sm text-gray-700">
                              Showing {users.length} {activeTab === 'clients' ? 'client' : 'user'}{users.length !== 1 ? 's' : ''}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Previous
                              </button>
                              <span className="px-4 py-2 text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="lg:hidden space-y-3 sm:space-y-4">
                      {loading ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                          Loading {activeTab === 'clients' ? 'clients' : 'users'}...
                        </div>
                      ) : users.length === 0 ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                          No {activeTab === 'clients' ? 'clients' : 'users'} found
                        </div>
                      ) : (
                        <>
                          {users.map((user) => (
                            <div key={user.user_id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                              <div className="flex items-start gap-3 sm:gap-4 mb-3">
                                <img
                                  src={getProfileImageUrl(user)}
                                  alt={user.name}
                                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                                      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
                                        <rect width="64" height="64" fill="#e5e7eb"/>
                                        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="24" font-weight="bold">
                                          ${user.name.charAt(0).toUpperCase()}
                                        </text>
                                      </svg>
                                    `)}`;
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">{user.name}</h3>
                                  {activeTab === 'clients' && user.organization_name && (
                                    <p className="text-xs sm:text-sm text-gray-500 truncate">{user.organization_name}</p>
                                  )}
                                  <p className="text-xs sm:text-sm text-gray-600 truncate">{user.email}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${
                                      user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {capitalizeStatus(user.status)}
                                    </span>
                                    {activeTab === 'users' && (
                                      <span className="text-xs sm:text-sm text-gray-600">{user.role_name}</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                                <div>
                                  <span className="text-gray-500">Contact:</span>
                                  <p className="text-gray-900 font-medium">{user.contact}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">City:</span>
                                  <p className="text-gray-900 font-medium">{user.city}</p>
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => navigate(`/users/view/${user.user_id}`)}
                                  className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center gap-1"
                                >
                                  <Eye size={14} /> View
                                </button>
                                {canEdit && (
                                  <button
                                    onClick={() => navigate(`/users/edit/${user.user_id}`)}
                                    className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center gap-1"
                                  >
                                    <Pencil size={14} /> Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => handleDelete(user.user_id, user.status, user.name)}
                                    className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Mobile Pagination */}
                          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                            <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                              Showing {users.length} {activeTab === 'clients' ? 'client' : 'user'}{users.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;
